import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { CornerDownLeft, Check, Ban } from "lucide-react";

// Self-contained, client-side demo of Smritium's cite-or-abstain behaviour.
// A tiny sample corpus + keyword rehydration. No network calls.
const CORPUS = [
  {
    file: "sync.md",
    hash: "a3f2c9",
    keywords: ["latency", "sla", "delivery", "agree", "agreed", "hours", "speed", "deadline"],
    source: "Standup, 3 Mar: we agreed a 6-hour delivery SLA, relaxed to 12 hours during the pilot.",
    answer: "You agreed a 6-hour delivery SLA — relaxed to 12 hours for the duration of the pilot.",
  },
  {
    file: "terms.md",
    hash: "77d01b",
    keywords: ["data", "live", "reside", "residency", "premises", "phi", "where", "stored", "stays", "leave"],
    source: "Contract §4: all data stays on the client's premises; no PHI leaves the network.",
    answer: "All data stays on the client's premises — no PHI ever leaves the network.",
  },
  {
    file: "eval.md",
    hash: "5be104",
    keywords: ["recall", "number", "accuracy", "benchmark", "metric", "score", "evaluation", "eval", "results"],
    source: "eval run 41: Recall@10 = 0.958 on the committed labelled set (semantic + temporal fusion).",
    answer: "Recall@10 reached 0.958 on the committed labelled set, using semantic + temporal fusion.",
  },
  {
    file: "roadmap.md",
    hash: "c10ffe",
    keywords: ["mcp", "bridge", "roadmap", "ship", "next", "agents", "when", "plan", "planned"],
    source: "roadmap: the MCP bridge ships after the ambient inbox; encrypted P2P sync is planned.",
    answer: "The MCP bridge ships after the ambient inbox; encrypted peer-to-peer sync is planned next.",
  },
];

const SUGGESTED = [
  "What did we agree on latency?",
  "Where does the data live?",
  "What's the Recall@10 number?",
  "When does the MCP bridge ship?",
  "What's the weather in Paris?",
];

const STOP = new Set(["the", "a", "an", "on", "in", "of", "to", "is", "our", "we", "what", "whats", "when", "where", "did", "do", "does", "s", "for"]);

function retrieve(query) {
  const tokens = query.toLowerCase().replace(/[^a-z0-9\s]/g, " ").split(/\s+/).filter((t) => t && !STOP.has(t));
  let best = null;
  let bestScore = 0;
  for (const doc of CORPUS) {
    let score = 0;
    for (const t of tokens) {
      if (doc.keywords.some((k) => k === t || k.startsWith(t) || t.startsWith(k))) score += 1;
    }
    if (score > bestScore) {
      bestScore = score;
      best = doc;
    }
  }
  return bestScore >= 1 ? best : null;
}

export const MemoryDemo = () => {
  const [q, setQ] = useState("");
  const [state, setState] = useState({ status: "idle", doc: null });

  const ask = (query) => {
    const text = (query ?? q).trim();
    if (!text) return;
    setQ(text);
    setState({ status: "thinking", doc: null });
    setTimeout(() => {
      const doc = retrieve(text);
      setState({ status: doc ? "answer" : "abstain", doc });
    }, 700);
  };

  return (
    <div className="border border-white/10 bg-ink-850" data-testid="memory-demo">
      <div className="flex items-center gap-2 border-b border-white/10 px-5 py-3 font-mono text-xs text-mist">
        <span className="h-2 w-2 rounded-full bg-saffron/70" />
        smritium · sample corpus (4 files, offline)
      </div>

      <div className="p-5 md:p-7">
        <div className="mb-5 flex flex-wrap gap-2">
          {SUGGESTED.map((s) => (
            <button
              key={s}
              onClick={() => ask(s)}
              data-testid="demo-suggested"
              className="border border-white/12 px-3 py-1.5 text-xs text-mist transition-colors duration-200 hover:border-saffron/40 hover:text-bone"
            >
              {s}
            </button>
          ))}
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            ask();
          }}
          className="flex items-center gap-2 border border-white/12 bg-white/[0.02] px-4 py-3"
        >
          <span className="font-mono text-sm text-saffron">?</span>
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Ask the corpus…"
            data-testid="demo-input"
            className="w-full bg-transparent font-mono text-sm text-bone placeholder:text-mist/60 outline-none"
          />
          <button type="submit" data-testid="demo-ask" className="text-mist transition-colors hover:text-bone" aria-label="Ask">
            <CornerDownLeft className="h-4 w-4" />
          </button>
        </form>

        <div className="mt-5 min-h-[132px]">
          <AnimatePresence mode="wait">
            {state.status === "thinking" && (
              <motion.div
                key="thinking"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex flex-wrap gap-3 font-mono text-xs text-mist"
              >
                {["semantic", "temporal", "relational"].map((s, i) => (
                  <motion.span
                    key={s}
                    initial={{ opacity: 0.3 }}
                    animate={{ opacity: [0.3, 1, 0.3] }}
                    transition={{ duration: 1, repeat: Infinity, delay: i * 0.15 }}
                    className="border border-white/10 px-2 py-1"
                  >
                    {s} ▸ rehydrating
                  </motion.span>
                ))}
              </motion.div>
            )}

            {state.status === "answer" && state.doc && (
              <motion.div key="answer" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} data-testid="demo-answer">
                <div className="flex items-start gap-3">
                  <Check className="mt-1 h-4 w-4 flex-shrink-0 text-signal" />
                  <p className="font-serif text-lg leading-snug text-bone">{state.doc.answer}</p>
                </div>
                <div className="mt-4 border-t border-white/10 pt-4">
                  <div className="font-mono text-[11px] uppercase tracking-[0.14em] text-mist">Source</div>
                  <div className="mt-2 flex items-start gap-3 font-mono text-xs text-mist">
                    <span className="text-saffron">[1] {state.doc.file}</span>
                    <span className="opacity-50">chunk {state.doc.hash}</span>
                  </div>
                  <p className="mt-2 max-w-xl text-xs italic leading-relaxed text-mist/80">“{state.doc.source}”</p>
                </div>
              </motion.div>
            )}

            {state.status === "abstain" && (
              <motion.div key="abstain" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} data-testid="demo-abstain">
                <div className="flex items-start gap-3">
                  <Ban className="mt-1 h-4 w-4 flex-shrink-0 text-mist" />
                  <p className="font-serif text-lg leading-snug text-bone">
                    I don't have enough in this corpus to answer that — so I abstain rather than invent a citation.
                  </p>
                </div>
                <p className="mt-4 border-t border-white/10 pt-4 font-mono text-xs text-mist">
                  No chunk cleared the confidence threshold. Cite-or-abstain, by design.
                </p>
              </motion.div>
            )}

            {state.status === "idle" && (
              <motion.p key="idle" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="font-mono text-xs text-mist">
                Pick a question above, or ask your own. Answers cite a source chunk — or abstain.
              </motion.p>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};
